import {
  Caption1,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  OverlayDrawer,
  makeStyles,
  tokens,
} from "@fluentui/react-components";
import { ArrowUpRight16Regular, Dismiss24Regular } from "@fluentui/react-icons";
import { useEffect } from "react";
import { FuiButton } from "@/components/fui-control";
import { Inspector } from "@/features/inspector/Inspector";
import type { CalendarEventKind } from "@/features/calendar-view/calendar-layout";
import { openScopeInSchedule } from "@/features/tasks/links";
import { useProjectStore } from "@/store/project-store";

const KIND_LABEL: Record<CalendarEventKind, string> = {
  wbs: "WBS",
  activity: "작업",
  milestone: "마일스톤",
  deadline: "마감",
  execution: "Task",
};

const useStyles = makeStyles({
  drawer: {
    width: "520px",
    maxWidth: "100vw",
  },
  backdrop: {
    backdropFilter: "blur(2.5px)",
    WebkitBackdropFilter: "blur(2.5px)",
    backgroundColor: "rgba(0, 0, 0, 0.16)",
  },
  header: {
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  headerTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    minHeight: "32px",
    width: "100%",
  },
  muted: {
    color: tokens.colorNeutralForeground3,
  },
  title: {
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase500,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  body: {
    padding: 0,
    minHeight: 0,
    flex: 1,
    overflow: "hidden",
  },
  footer: {
    display: "flex",
    justifyContent: "flex-end",
  },
});

export function SchedulePeekDrawer({
  taskId,
  kind,
  onClose,
  onViewTasks,
}: {
  taskId: string | null;
  kind: CalendarEventKind | null;
  onClose: () => void;
  onViewTasks?: (scheduleTaskId: string) => void;
}) {
  const styles = useStyles();
  const projectId = useProjectStore((s) => s.activeId) ?? "";
  const task = useProjectStore((s) => (taskId ? s.project.tasks.find((t) => t.id === taskId) : undefined));

  useEffect(() => {
    if (!taskId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      e.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [taskId, onClose]);

  useEffect(() => {
    if (taskId && !task) onClose();
  }, [taskId, task, onClose]);

  if (!taskId || !task) return null;

  const goSchedule = () => {
    openScopeInSchedule(projectId, taskId);
    onClose();
  };

  return (
    <OverlayDrawer
      open
      position="end"
      modalType="alert"
      className={styles.drawer}
      backdrop={{
        className: styles.backdrop,
        onMouseDown: (e) => e.preventDefault(),
      }}
      onOpenChange={(e, d) => {
        e.preventDefault();
        if (d.type === "backdropClick") return;
        if (!d.open) onClose();
      }}
      aria-label="일정 상세"
    >
      <DrawerHeader className={styles.header}>
        <div className={styles.headerTop}>
          <Caption1 className={styles.muted}>{kind ? KIND_LABEL[kind] : "일정"} 상세</Caption1>
          <FuiButton appearance="subtle" icon={<Dismiss24Regular />} aria-label="닫기" onClick={onClose} />
        </div>
        <div className={styles.title} title={task.name}>
          {task.name || "이름 없음"}
        </div>
      </DrawerHeader>
      <DrawerBody className={styles.body}>
        <Inspector taskId={taskId} onViewTasks={onViewTasks} />
      </DrawerBody>
      <DrawerFooter className={styles.footer}>
        <FuiButton appearance="secondary" icon={<ArrowUpRight16Regular />} onClick={goSchedule}>
          일정 탭에서 보기
        </FuiButton>
      </DrawerFooter>
    </OverlayDrawer>
  );
}
